import { useMemo } from "react";
import { useSelector } from "react-redux";
import usePeriodicStats from "@shared/features/bots/BotForm/Backtesting/usePeriodicStats";
import { BacktestingTimeframe, MarketplaceSubGroup } from "@shared/types";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { getNetPnlChange } from "@shared/features/newMarketplace/utils";

export interface PnlPoint {
  /** Unix seconds. */
  time: number;
  /** Net PnL in percent of the starting funds. */
  value: number;
}

/**
 * A channel's net PnL over a backtesting timeframe, in percent of the
 * starting funds (the unit of the figure shown next to it), plus that figure.
 */
const useNetPnlSeries = (
  group: MarketplaceSubGroup | undefined,
  timeframe: BacktestingTimeframe,
  numPoints: number
) => {
  const displayCurrency = useSelector(selectDisplayCurrency);
  const backtestingRun = group?.timeframe_to_backtesting_run_data?.[timeframe];
  const { netPnlLineChart } = usePeriodicStats({
    backtestingRun,
    periodicStatsData: backtestingRun?.periodic_stats,
    isGraphMinified: true,
    numPoints,
  });
  const initialFunds =
    backtestingRun?.initial_portfolio_size?.[displayCurrency];

  const points = useMemo<PnlPoint[]>(
    () =>
      netPnlLineChart
        .filter((point) => point && Number.isFinite(point.y))
        .map((point) => ({
          time: Number(point.rawX),
          value: initialFunds ? (point.y / initialFunds) * 100 : point.y,
        })),
    [netPnlLineChart, initialFunds]
  );

  return {
    points,
    change: getNetPnlChange(group, timeframe, displayCurrency),
  };
};

export default useNetPnlSeries;
